import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, Coffee, Download, LogOut, Share, X } from "lucide-react";
import { api } from "@/lib/api";
import { usePWAManifest } from "@/hooks/usePWAManifest";

const KEY = "bk_staff_session";
const staffApi = (token) => ({
  get: (p) => api.get(p, { headers: { "X-Staff-Token": token } }),
  post: (p, b) => api.post(p, b, { headers: { "X-Staff-Token": token } }),
});
const money = (n) => `$${(n || 0).toFixed(2)}`;
const hm = (h) => {
  const m = Math.round((h || 0) * 60);
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};
const t = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—");
const dayLabel = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
const shiftDate = (iso) => new Date(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const input = "w-full px-4 py-3 rounded-2xl border-2 border-stone-200 bg-white text-lg font-mono tracking-widest text-center outline-none focus:border-brand-red";

function InstallButton() {
  const pwa = usePWAManifest({ manifest: "/manifest-hours.json", themeColor: "#9B2226", appleTitle: "My Hours" });
  const [help, setHelp] = useState(false);
  if (pwa.isStandalone) return null;
  const onClick = async () => {
    if (pwa.installEvent) {
      if (!(await pwa.install())) setHelp(true);
    } else setHelp(true);
  };
  return (
    <>
      <button type="button" onClick={onClick} data-testid="myhours-install-btn"
        className="flex items-center gap-1.5 bg-white text-brand-red px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-black active:scale-95">
        <Download className="w-3.5 h-3.5" /> Install
      </button>
      {help && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={() => setHelp(false)} data-testid="myhours-install-help">
          <div className="bg-white text-stone-800 rounded-3xl p-6 w-full max-w-sm space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-black text-lg flex items-center gap-2"><Download className="w-5 h-5 text-brand-red" /> Install My Hours</div>
              <button type="button" onClick={() => setHelp(false)} aria-label="Close" className="text-stone-400"><X className="w-5 h-5" /></button>
            </div>
            {pwa.isIOS ? (
              <ol className="text-sm list-decimal pl-5 space-y-2">
                <li>Tap the <Share className="w-4 h-4 inline -mt-1" /> <b>Share</b> button in Safari.</li>
                <li>Tap <b>Add to Home Screen</b>, then <b>Add</b>.</li>
              </ol>
            ) : (
              <ol className="text-sm list-decimal pl-5 space-y-2">
                <li>Open the browser menu (<b>⋮</b>).</li>
                <li>Tap <b>Install app</b> or <b>Add to Home screen</b>.</li>
              </ol>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function LoginScreen({ onLogin }) {
  const [pin, setPin] = useState("");
  const [last4, setLast4] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/staff/login", { pin, phone_last4: last4 });
      localStorage.setItem(KEY, JSON.stringify(data));
      onLogin(data);
    } catch (error) {
      const d = error?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Couldn't sign in — check your connection.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex-1 flex flex-col justify-center px-6 max-w-sm w-full mx-auto space-y-4" data-testid="myhours-login">
      <div>
        <h1 className="text-2xl font-black text-stone-900">Sign in</h1>
        <p className="text-stone-500 text-sm mt-1">Use your clock-in PIN and the last 4 digits of your phone number. You&apos;ll only ever see your own hours.</p>
      </div>
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Your 4-digit PIN</span>
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" autoComplete="off" type="password" className={input} data-testid="myhours-pin" required />
      </label>
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Last 4 of your phone</span>
        <input value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" autoComplete="off" className={input} data-testid="myhours-last4" required />
      </label>
      {err && <div className="text-red-600 text-sm font-semibold" data-testid="myhours-login-error">{err}</div>}
      <button type="submit" disabled={busy || pin.length !== 4 || last4.length !== 4} data-testid="myhours-login-submit"
        className="w-full bg-brand-red text-white py-4 rounded-full text-sm uppercase tracking-widest font-black disabled:opacity-40">
        {busy ? "Signing in…" : "See my hours"}
      </button>
    </form>
  );
}

function WeekView({ data, onWeek }) {
  return (
    <div className="px-5 pb-10 space-y-5 max-w-md w-full mx-auto" data-testid="myhours-week">
      <div className="bg-stone-900 text-white rounded-3xl p-5">
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => onWeek(addDays(data.week_start, -7))} aria-label="Previous week" data-testid="myhours-prev-week" className="p-2 rounded-full bg-stone-800"><ChevronLeft className="w-5 h-5" /></button>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">{data.is_current_week ? "This week" : "Week of"}</div>
            <div className="font-bold text-sm" data-testid="myhours-week-range">{dayLabel(data.week_start)} – {dayLabel(data.week_end)}</div>
          </div>
          <button type="button" onClick={() => onWeek(addDays(data.week_start, 7))} disabled={data.is_current_week} aria-label="Next week" data-testid="myhours-next-week" className="p-2 rounded-full bg-stone-800 disabled:opacity-30"><ChevronRight className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Hours worked</div>
            <div className="text-3xl font-black tabular-nums" data-testid="myhours-total-hours">{hm(data.total_hours)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Estimated pay</div>
            <div className="text-3xl font-black tabular-nums text-brand-yellow" data-testid="myhours-estimated-pay">{money(data.estimated_pay)}</div>
            <div className="text-[10px] text-stone-400">at {money(data.pay_rate)}/hr · before taxes</div>
          </div>
        </div>
        {data.on_clock && (
          <div className="mt-4 text-xs font-bold flex items-center gap-1.5 text-emerald-300" data-testid="myhours-on-clock">
            {data.on_break ? <Coffee className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
            {data.on_break ? "You're on break — hours pause until you end it." : "You're clocked in — this total is still counting."}
          </div>
        )}
      </div>

      <div className="bg-white rounded-3xl border border-stone-200 overflow-hidden">
        <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-stone-500 font-bold border-b border-stone-100">By day</div>
        <ul className="divide-y divide-stone-100">
          {data.days.map((d) => (
            <li key={d.date} className={`px-4 py-2.5 flex items-center justify-between text-sm ${d.is_future ? "text-stone-300" : ""}`} data-testid={`myhours-day-${d.date}`}>
              <span className={`font-bold ${d.is_today ? "text-brand-red" : ""}`}>{dayLabel(d.date)}{d.is_today ? " · today" : ""}</span>
              <span className="tabular-nums">{d.hours > 0 ? `${hm(d.hours)} · ${money(d.pay)}` : d.is_future ? "" : "—"}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white rounded-3xl border border-stone-200 overflow-hidden">
        <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-stone-500 font-bold border-b border-stone-100">Punches</div>
        {data.punches.length === 0 ? (
          <div className="p-5 text-center text-stone-400 text-sm" data-testid="myhours-no-punches">No shifts this week.</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {data.punches.map((p) => (
              <li key={p.id} className="px-4 py-3 text-sm" data-testid={`myhours-punch-${p.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold">{shiftDate(p.clock_in)}</span>
                  <span className="font-mono font-black">{hm(p.hours)}</span>
                </div>
                <div className="text-stone-500 text-xs mt-0.5">
                  {t(p.clock_in)} → {p.open ? <span className="text-emerald-700 font-bold">still clocked in</span> : t(p.clock_out)}
                  {p.break_minutes > 0 && ` · ${p.break_minutes} min break (unpaid)`}
                  {p.edited && " · adjusted by manager"}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="text-[11px] text-stone-400 text-center">Estimated pay is hours × your rate, before taxes and deductions. Questions? Ask the owner.</div>
    </div>
  );
}

export default function MyHoursPage() {
  const [session, setSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
  });
  const [data, setData] = useState(null);
  const [week, setWeek] = useState(null);
  const [err, setErr] = useState("");

  const signOut = useCallback(async () => {
    try { if (session?.token) await staffApi(session.token).post("/staff/logout", {}); } catch {}
    localStorage.removeItem(KEY);
    setSession(null);
    setData(null);
  }, [session]);

  useEffect(() => {
    if (!session?.token) return;
    let cancelled = false;
    setErr("");
    staffApi(session.token).get(`/staff/hours${week ? `?week=${week}` : ""}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 401) { localStorage.removeItem(KEY); setSession(null); }
        else setErr("Couldn't load your hours — check your connection.");
      });
    return () => { cancelled = true; };
  }, [session, week]);

  return (
    <div className="min-h-screen bg-stone-100 flex flex-col" data-testid="myhours-page" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <header className="bg-brand-red text-white px-4 py-4 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-black leading-none whitespace-nowrap">My Hours</div>
          <div className="text-[10px] uppercase tracking-widest opacity-80 whitespace-nowrap">{session ? session.name : "Blossom's Kitchen"}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <InstallButton />
          {session && (
            <button type="button" onClick={signOut} data-testid="myhours-signout" className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-black bg-red-900/50 px-3 py-1.5 rounded-full">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          )}
        </div>
      </header>
      {!session ? (
        <LoginScreen onLogin={setSession} />
      ) : err ? (
        <div className="p-6 text-center text-red-600 text-sm" data-testid="myhours-error">{err}</div>
      ) : !data ? (
        <div className="p-6 text-center text-stone-400 text-sm">Loading your week…</div>
      ) : (
        <div className="pt-4"><WeekView data={data} onWeek={setWeek} /></div>
      )}
    </div>
  );
}
